<template>
    <LoadingComponent :props="loading" />

    <div class="col-12">
        <div class="db-card">
            <div class="db-card-header border-none">
                <h3 class="db-card-title pt-1">{{ $t('menu.ai_imports') }}</h3>
                <div class="db-card-filter">
                    <TableLimitComponent :method="list" :search="search" :page="paginationPage" />
                    <router-link :to="{ name: 'admin.products' }" class="db-btn py-2 bg-gray-200 hover:opacity-80">
                        <i class="lab lab-line-long-arrow-left"></i>
                        <span>{{ $t('button.products') }}</span>
                    </router-link>
                    <button
                        type="button"
                        class="db-btn h-9 py-2 px-3 bg-gray-200 hover:opacity-80"
                        @click="refreshList"
                        :disabled="isRefreshingList"
                        :class="{ 'disabled:opacity-50': isRefreshingList }"
                    >
                        <template v-if="isRefreshingList">
                            <svg class="inline-block animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                            </svg>
                        </template>
                        <template v-else>
                            <i class="fa-solid fa-arrows-rotate"></i>
                        </template>
                    </button>
                    <button
                        type="button"
                        class="db-btn py-2 text-white bg-primary hover:opacity-80"
                        @click="newImport"
                        v-if="permissionChecker('products_create')"
                    >
                        <i class="lab lab-line-upload-file"></i>
                        <span>{{ $t('button.new_import') }}</span>
                    </button>
                </div>
            </div>

            <div class="db-table-responsive">
                <table class="db-table stripe">
                    <thead class="db-table-head">
                        <tr class="db-table-head-tr">
                            <th class="db-table-head-th">{{ $t('label.code') }}</th>
                            <th class="db-table-head-th">{{ $t('label.items') }}</th>
                            <th class="db-table-head-th">{{ $t('label.incompletes') }}</th>
                            <th class="db-table-head-th">{{ $t('label.created_date') }}</th>
                            <th class="db-table-head-th">{{ $t('label.start_date') }}</th>
                            <th class="db-table-head-th">{{ $t('label.processed_date') }}</th>
                            <th class="db-table-head-th">{{ $t('label.status') }}</th>
                            <th class="db-table-head-th text-right">{{ $t('label.action') }}</th>
                        </tr>
                    </thead>
                    <tbody class="db-table-body" v-if="imports.length">
                        <tr class="db-table-body-tr" v-for="item in imports" :key="item.id">
                            <td class="db-table-body-td">{{ item.code }}</td>
                            <td class="db-table-body-td">{{ item.total_items }}</td>
                            <td class="db-table-body-td">{{ incompleteCount(item) }}</td>
                            <td class="db-table-body-td">{{ simpleDateTime(item.created_at) }}</td>
                            <td class="db-table-body-td">{{ simpleDateTime(item.started_at) }}</td>
                            <td class="db-table-body-td">{{ simpleDateTime(item.processed_at) }}</td>
                            <td class="db-table-body-td">
                                <span :class="statusClass(item.status)">{{ statusLabel(item.status) }}</span>
                            </td>
                            <td class="db-table-body-td">
                                <div class="flex justify-end items-center gap-1.5">
                                    <button
                                        v-if="canPurgeImport(item)"
                                        type="button"
                                        class="db-btn-outline sm default danger transparent hover:opacity-80 disabled:opacity-50 disabled:disabled"
                                        :disabled="isPurgingImport(item.id)"
                                        @click="openPurgeModal(item)"
                                    >
                                        <svg v-if="isPurgingImport(item.id)" class="inline-block animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                                            <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                                            <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                                        </svg>
                                        <i v-else class="lab lab-line-trash text-md"></i>
                                    </button>
                                    <router-link
                                        v-if="!isInProgress(item.status)"
                                        :to="{ name: 'admin.products.aiImport.show', params: { id: item.id } }"
                                        class="db-btn-outline sm default primary transparent hover:opacity-80"
                                    >
                                        <i class="fa-solid fa-arrow-right"></i>
                                        <span class="db-tooltip">{{ $t('button.view') }}</span>
                                    </router-link>
                                    <button
                                        v-else
                                        type="button"
                                        class="db-btn-outline sm default transparent hover:opacity-80 disabled:opacity-50 disabled:disabled"
                                        disabled
                                        :title="$t('label.in_progress')"
                                    >
                                        <i class="fa-solid fa-ban"></i>
                                    </button>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="flex items-center justify-between border-t px-4 py-4">
                <PaginationSMBox :pagination="pagination" :method="list" />
                <div class="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                    <PaginationTextComponent :props="{ page: paginationPage }" />
                    <PaginationBox :pagination="pagination" :method="list" />
                </div>
            </div>
        </div>
    </div>

    <div v-if="purgeModal.visible" class="fixed inset-0 z-50 flex items-center justify-center bg-light/50 p-4 modal-backdrop" @click.self="closePurgeModal">
        <div class="w-full max-w-xl rounded-lg bg-white shadow-md border">
            <div class="relative px-4 pt-4 pb-2 border-b">
                <h4 class="text-lg font-medium font-client text-danger">{{ $t('label.delete_failed_import') }}</h4>
                <button type="button" class="absolute right-3 top-4 text-gray-500 hover:text-gray-700" @click="closePurgeModal">
                    <i class="lab lab-fill-close"></i>
                </button>
            </div>

            <div class="px-5 py-4 text-sm space-y-2">
                <p class="font-client text-sm">
                    {{ $t('message.ai_import_purge_modal_intro_prefix') }}
                    <strong>#{{ purgeModal.code }}</strong>
                    {{ $t('message.ai_import_purge_modal_intro_suffix') }}
                </p>
                <hr class="w-full border-gray-100 my-4" />
                <p class="font-admin text-sm">
                    {{ $t('message.ai_import_purge_modal_items_detected_prefix') }} <strong>{{ Number(purgeModal.totalItems || 0) }}</strong>
                </p>
                <hr class="w-full border-gray-100 my-4" />
                <p class="font-client text-xs mb-2">
                    {{ $t('message.ai_import_purge_modal_safe_delete') }}
                </p>
                <p class="text-danger font-medium font-client text-sm">
                    {{ $t('message.ai_import_purge_modal_irreversible') }}
                </p>
            </div>

            <div class="px-4 py-3 border-t flex justify-end gap-2">
                <button type="button" class="db-btn py-2 px-4 bg-gray-200" @click="closePurgeModal" :disabled="isPurgingImport(purgeModal.importId)">
                    <i class="lab lab-fill-close"></i>
                    <span>{{ $t('button.cancel') }}</span>
                </button>
                <button
                    type="button"
                    class="db-btn py-2 px-4 text-white bg-danger disabled:opacity-50 disabled:bg-gray"
                    :disabled="isPurgingImport(purgeModal.importId)"
                    @click="confirmPurgeModal"
                >
                    <template v-if="isPurgingImport(purgeModal.importId)">
                        <svg class="inline-block mr-1 animate-spin" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                            <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="3" opacity="0.25"></circle>
                            <path d="M22 12a10 10 0 0 1-10 10" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"></path>
                        </svg>
                        <span>{{ $t('button.processing') }}</span>
                    </template>
                    <template v-else>
                        <i class="lab lab-line-trash"></i>
                        <span>{{ $t('button.confirm_and_delete') }}</span>
                    </template>
                </button>
            </div>
        </div>
    </div>

    <AiImportFileUploadComponent @created="onCreated" />
</template>

<script>
import LoadingComponent from '../../components/LoadingComponent';
import PaginationTextComponent from '../../components/pagination/PaginationTextComponent';
import PaginationBox from '../../components/pagination/PaginationBox';
import PaginationSMBox from '../../components/pagination/PaginationSMBox';
import TableLimitComponent from '../../components/TableLimitComponent';
import alertService from '../../../../services/alertService';
import appService from '../../../../services/appService';
import AiImportFileUploadComponent from './AiImportFileUploadComponent';

export default {
    name: 'AiImportListComponent',
    components: {
        LoadingComponent,
        PaginationTextComponent,
        PaginationBox,
        PaginationSMBox,
        TableLimitComponent,
        AiImportFileUploadComponent,
    },
    data() {
        return {
            loading: {
                isActive: false,
            },
            search: {
                paginate: 1,
                page: 1,
                per_page: 10,
                order_column: 'id',
                order_type: 'desc',
            },
            autoRefreshTimer: null,
            autoRefreshMs: 7000,
            isRefreshingList: false,
            purgingImportId: null,
            purgeModal: {
                visible: false,
                importId: null,
                code: '',
                totalItems: 0,
            },
        };
    },
    computed: {
        imports() {
            return this.$store.getters['aiImport/lists'];
        },
        pagination() {
            return this.$store.getters['aiImport/pagination'];
        },
        paginationPage() {
            return this.$store.getters['aiImport/page'];
        },
        hasInProgressImport() {
            return this.imports.some((item) => this.isInProgress(item?.status));
        },
    },
    watch: {
        hasInProgressImport: {
            immediate: true,
            handler(value) {
                if (value) {
                    this.startAutoRefresh();
                    return;
                }

                this.stopAutoRefresh();
            },
        },
    },
    mounted() {
        this.list();
    },
    beforeUnmount() {
        this.stopAutoRefresh();
    },
    methods: {
        permissionChecker(permissionName) {
            return appService.permissionChecker(permissionName);
        },
        list(page = 1, { silent = false } = {}) {
            if (!silent) {
                this.loading.isActive = true;
            }

            this.search.page = page;
            return this.$store.dispatch('aiImport/lists', this.search).finally(() => {
                if (!silent) {
                    this.loading.isActive = false;
                }
            });
        },
        async refreshList() {
            if (this.isRefreshingList) {
                return;
            }

            this.isRefreshingList = true;
            try {
                await this.list(this.search.page || 1, { silent: true });
            } finally {
                this.isRefreshingList = false;
            }
        },
        startAutoRefresh() {
            if (this.autoRefreshTimer) {
                return;
            }

            this.autoRefreshTimer = setInterval(() => {
                this.list(this.search.page, { silent: true });
            }, this.autoRefreshMs);
        },
        stopAutoRefresh() {
            if (!this.autoRefreshTimer) {
                return;
            }

            clearInterval(this.autoRefreshTimer);
            this.autoRefreshTimer = null;
        },
        isInProgress(status) {
            return Number(status) === 10;
        },
        isFailed(status) {
            return Number(status) === 30;
        },
        canPurgeImport(item) {
            return this.permissionChecker('products_edit') && !!item?.can_purge;
        },
        isPurgingImport(importId) {
            return Number(this.purgingImportId) === Number(importId);
        },
        openPurgeModal(item) {
            if (!this.canPurgeImport(item) || this.isPurgingImport(item.id)) {
                return;
            }

            this.purgeModal.visible = true;
            this.purgeModal.importId = item.id;
            this.purgeModal.code = item.code || item.id;
            this.purgeModal.totalItems = Number(item.total_items || 0);
        },
        closePurgeModal() {
            if (this.isPurgingImport(this.purgeModal.importId)) {
                return;
            }
            this.resetPurgeModal();
        },

        resetPurgeModal() {
            this.purgeModal.visible = false;
            this.purgeModal.importId = null;
            this.purgeModal.code = '';
            this.purgeModal.totalItems = 0;
        },
        async confirmPurgeModal() {
            if (!this.purgeModal.importId) {
                return;
            }

            await this.purgeFailedImport({ id: this.purgeModal.importId });
        },
        async purgeFailedImport(item) {
            if (!item?.id || this.isPurgingImport(item.id)) {
                return;
            }

            this.purgingImportId = item.id;
            try {
                await this.$store.dispatch('aiImport/purgeFailed', item.id);
                alertService.success(this.$t('message.ai_import_purged'));
                this.resetPurgeModal();
                this.list(this.search.page || 1, { silent: true });
            } catch (err) {
                alertService.error(err.response?.data?.message || err.message);
            } finally {
                this.purgingImportId = null;
            }
        },
        newImport() {
            appService.modalShow('#aiImportFileModal');
        },
        onCreated(importData) {
            if (importData?.id) {
                this.$router.push({ name: 'admin.products.aiImport.show', params: { id: importData.id } });
                return;
            }
            this.list();
        },
        simpleDateTime(value) {
            if (!value) {
                return '--';
            }
            return appService.convertOnlyDateTime(value);
        },
        statusLabel(status) {
            const statuses = {
                5: this.$t('label.pending'),
                10: this.$t('label.in_progress'),
                15: this.$t('label.processed_complete'),
                20: this.$t('label.processed_incomplete'),
                25: this.$t('label.confirmed'),
                30: this.$t('label.failed'),
            };

            return statuses[status] || status;
        },
        statusClass(status) {
            if (status === 5) return 'db-badge py-0.5 border border-gray-600 bg-gray-100 text-gray-600';
            if (status === 10) return 'db-badge py-0.5 border border-amber-500 bg-amber-100 text-amber-500';
            if (status === 15) return 'db-badge py-0.5 border border-blue-500 text-blue-500 bg-blue-100';
            if (status === 20) return 'db-badge py-0.5 border border-warning bg-orange-100 text-orange-500';
            if (status === 25) return 'db-badge py-0.5 border border-gray-600 text-gray-600';
            if (status === 30) return 'db-badge py-0.5 border border-red-500 bg-red-100 text-red-500';
            return 'db-badge py-0.5 text-white bg-gray-600';
        },
        incompleteCount(item) {
            const directCount = Number(item?.incomplete_items_count ?? item?.incomplete_items ?? item?.incomplete_count);
            if (Number.isFinite(directCount) && directCount >= 0) {
                return directCount;
            }

            if (Array.isArray(item?.items)) {
                return item.items.filter((entry) => Number(entry?.status) === 20 || !!entry?.is_incomplete).length;
            }

            return 0;
        },
    },
};
</script>
